import { useRef } from "react";

/**
 * Attach long-press behavior (~600ms hold) to any element. Cancels on move/leave.
 * Returns the props to spread on the element and a helper to cancel.
 */
export default function useLongPress(onLongPress, { delay = 600, moveTolerance = 10 } = {}) {
  const timerRef = useRef(null);
  const firedRef = useRef(false);
  const startRef = useRef({ x: 0, y: 0 });

  const clear = () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
  };

  const start = (e) => {
    firedRef.current = false;
    const point = e.touches?.[0] || e;
    startRef.current = { x: point.clientX ?? 0, y: point.clientY ?? 0 };
    clear();
    timerRef.current = setTimeout(() => {
      firedRef.current = true;
      onLongPress(e);
    }, delay);
  };

  const move = (e) => {
    if (!timerRef.current) return;
    const point = e.touches?.[0] || e;
    const dx = (point.clientX ?? 0) - startRef.current.x;
    const dy = (point.clientY ?? 0) - startRef.current.y;
    if (Math.abs(dx) > moveTolerance || Math.abs(dy) > moveTolerance) clear();
  };

  const end = () => { clear(); };

  return {
    props: {
      onMouseDown: start,
      onMouseMove: move,
      onMouseUp: end,
      onMouseLeave: end,
      onTouchStart: start,
      onTouchMove: move,
      onTouchEnd: end,
      onTouchCancel: end,
      onContextMenu: (e) => e.preventDefault(),
    },
    firedRef,
  };
}
